import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Position } from '@typeRoots/common/UI';
import { RootState } from 'src/store/store';

interface UIState {
  mapAddress: string;
  mapPositon: Position;
  autoCompleteAdress: string;
  activeTab: number;
  filledTabsIndex: number;
  accountType: string | null;
  tpJwtToken: string | null;
  isBannerVariantSelected: boolean; // Optional field for GrowthBook variant
  bannerVariant?: string; // Optional field for GrowthBook variant
  showGlobalLoader: boolean; // Add this line
  lastQuoteScreen: 'PURCHASE' | 'SUCCESS' | null; // Tracks the last quote screen
  insuranceRouteSource: string | null; // Global insurance route source variable
  accountSource: string; // Account source from routing module (e.g., TRUCKER_PATH_APP, TRUCK_LOADS)
}

const initialState: UIState = {
  mapAddress: '',
  mapPositon: { lat: 0, lng: 0 },
  autoCompleteAdress: 'USA',
  activeTab: 0,
  filledTabsIndex: 0,
  accountType: null,
  tpJwtToken: null,
  isBannerVariantSelected: false, // Added missing property
  bannerVariant: '', // Optional field for GrowthBook variant
  showGlobalLoader: false, // Add this line
  lastQuoteScreen: null, // Initialize as null
  insuranceRouteSource: null, // Initialize insurance route source
  accountSource: 'TRUCKER_PATH_APP', // Default account source
};

export const UISlice = createSlice({
  name: 'UI',
  initialState,
  reducers: {
    setMapAddress(state, action: PayloadAction<Partial<UIState>>) {
      // Correct way: mutate the state directly
      state.mapAddress = action.payload.mapAddress ?? state.mapAddress;
      state.mapPositon = action.payload.mapPositon ?? state.mapPositon;
      state.autoCompleteAdress = action.payload.autoCompleteAdress ?? state.autoCompleteAdress;
    },

    updateMapAddress(state, action: PayloadAction<Partial<UIState>>) {
      // Correct way: mutate the state directly
      state.mapAddress = action.payload.mapAddress ?? state.mapAddress;
      state.mapPositon = action.payload.mapPositon ?? state.mapPositon;
      state.autoCompleteAdress = action.payload.autoCompleteAdress ?? state.autoCompleteAdress;
    },
    setActiveTab(state, action: PayloadAction<Partial<UIState>>) {
      state.activeTab = action.payload.activeTab ?? state.activeTab;
    },
    setFilledTabsIndex(state, action: PayloadAction<Partial<UIState>>) {
      //handle the logic of settingUp current filled tab
      //Something like, if active tab > filledTabsIndex then change it, otherwise keep it same.
      if (
        action.payload.filledTabsIndex &&
        action.payload.filledTabsIndex > state.filledTabsIndex
      ) {
        state.filledTabsIndex = action.payload.filledTabsIndex as number;
      }
      //state.filledTabsIndex = action.payload.activeTab ?? state.activeTab;
    },
    resetTabs(state) {
      state.activeTab = 0;
      state.filledTabsIndex = 0;
    },
    // Add reducers for accountType
    setAccountType(state, action: PayloadAction<string>) {
      state.accountType = action.payload;
    },
    // clearAccountType(state) {
    //   state.accountType = null;
    // },

    setTpJwtToken(state, action: PayloadAction<string | null>) {
      state.tpJwtToken = action.payload; // Update the tpJwtToken in the state
    },
    setIsBannerVariantSelected(state, action: PayloadAction<boolean>) {
      state.isBannerVariantSelected = action.payload;
    },
    setBannerVariant(state, action: PayloadAction<string>) {
      state.bannerVariant = action.payload; // Update the accountType in the state
    },
    setShowGlobalLoader(state, action: PayloadAction<boolean>) {
      state.showGlobalLoader = action.payload;
    },
    setLastQuoteScreen(state, action: PayloadAction<'PURCHASE' | 'SUCCESS'>) {
      state.lastQuoteScreen = action.payload;
    },
    resetLastQuoteScreen(state) {
      state.lastQuoteScreen = null;
    },
    setInsuranceRouteSource(state, action: PayloadAction<string | null>) {
      state.insuranceRouteSource = action.payload;
    },
    setAccountSource(state, action: PayloadAction<string>) {
      state.accountSource = action.payload;
    },
  },
});

export const {
  setMapAddress,
  updateMapAddress,
  setActiveTab,
  setFilledTabsIndex,
  resetTabs,
  setAccountType,
  // clearAccountType,
  setTpJwtToken,
  setIsBannerVariantSelected,
  setBannerVariant,
  setShowGlobalLoader,
  setLastQuoteScreen,
  resetLastQuoteScreen,
  setInsuranceRouteSource,
  setAccountSource,
} = UISlice.actions;

export const selectLastQuoteScreen = (state: RootState) => state.UI.lastQuoteScreen;

export default UISlice.reducer;
